import React, { useEffect, useState } from "react";
import { filter, includes, find } from "lodash";
import {LeftTabsDrawer, useTheme} from "components/Mui";
import Loading from "components/Loading/Loading";

import PersonalInformation from "components/EmployeeDetailsForms/PersonalInformation/PersonalInformation";
import WorkScheduleTemplates from "containers/EmployeeDetails/WorkScheduleTemplates/WorkScheduleTemplates";
import Teams from "containers/EmployeeDetails/Teams/Teams";
import Roles from "components/EmployeeDetailsForms/Roles/Roles";
import CustomizeTimeOff from "components/EmployeeDetailsForms/CustomizeTimeOff/CustomizeTimeOff";

import Benefits from "./Benefits/Benefits";
import AttendanceReport from "./AttendanceReport/AttendanceReport";
import Wfh from "./Wfh/Wfh";
import TimeOff from "./TimeOff/TimeOff";
import DeactivateProfile from "./ProfileActions/DeactivateProfile";
import ReactivateProfile from "./ProfileActions/ReactivateProfile";
import DeleteProfile from "./ProfileActions/DeleteProfile";

import { formatDate, getContractBounds } from "utility/helpers/utils";

import {
    EventNoteOutlinedIcon,
    HouseOutlinedIcon,
    InfoOutlinedIcon,
    ManageAccountsOutlinedIcon,
    PeopleOutlineIcon,
    PoolOutlinedIcon,
    RedeemOutlinedIcon,
    AccessAlarmOutlinedIcon,
    PersonOffSharpIcon,
    PersonSharpIcon,
    PersonRemoveSharpIcon,
} from "components/Mui/Icons";
import { getEmployeeDetails, getEmployeeContracts } from "utility/apis/EmployeeService";

import "./EmployeeDetails.scss";
import {getIsManager} from "utility/helpers/authHelper";

const getEmployeeDrawerList = (
    employeeId,
    handleCloseButton,
    employee,
    managersInfo,
    setEmployeeUpdated,
    handleTabOnChange,
    handelRefreshEmployeeList,
    profileTabs,
    colorTheme,
    balanceTab,
    contractId,
    contracts,
    handleContractChange,
    contract,
    innerTabIndex,
    onInnerTabChange
) => {
    const isManager = getIsManager();
    let teams = [], holidays = [], isApproval = false, roles = [];
    if(employeeId){
        teams = (employee?.employeeTeams || []).map(team => team?.team);
        holidays = employee?.holidays.map(({ id }) => id);
        roles = employee?.roles;
        isApproval = !!find(roles, ({ id }) => (id === 1 || id === 2 || id === 5));
    }
    // Calculations stop at whichever comes first: the employee's deletion date, or the end of the
    // selected contract. Whether that date has already passed decides the tense — a contract that
    // ends next year reads "will stop", not "stopped".
    const contractEnd = getContractBounds(contract).end;
    const stopCandidates = [
        employee?.deletionDate ? new Date(employee.deletionDate).getTime() : null,
        // Guard against the "no end date" sentinels that reach us as epoch 0 / year 1899.
        contractEnd !== null && contractEnd > 0 && new Date(contractEnd).getFullYear() > 1900
            ? contractEnd
            : null,
    ].filter(Number.isFinite);
    const stopsAt = stopCandidates.length ? Math.min(...stopCandidates) : null;

    let message;
    if (!employee.activated || stopsAt !== null) {
        message = {
            word: stopsAt !== null && stopsAt > Date.now()
                ? "calculations-end-note-future"
                : "deactivated-profile-note",
            data: { date: formatDate(stopsAt !== null ? stopsAt : employee.deletionDate) },
        };
    }
    let tabs = [
        {
            id: "personal_info",
            title: "Personal Info",
            component: (
                <PersonalInformation
                    setEmployeeUpdated={setEmployeeUpdated}
                    title="Personal Info"
                    employeeId={employeeId}
                    handleCloseButton={handleCloseButton}
                    message={message}
                    employee={employee}
                    newProfile
                />
            ),
            icon: <InfoOutlinedIcon />,
        },
        {
            id: "roles",
            title: "Work-related Information",
            component: (
                <Roles
                    title="Work-related Information"
                    employeeId={employeeId}
                    handleCloseButton={handleCloseButton}
                    roles={roles}
                    employee={employee}
                    setEmployeeUpdated={setEmployeeUpdated}
                    message={message}
                />
            ),
            icon: <ManageAccountsOutlinedIcon />,
        },
        {
            id: "work_schedule_templates",
            title: "Work Schedule Templates",
            component: (
                <WorkScheduleTemplates
                    contractId={contractId}
                    contract={contract}
                    contracts={contracts}
                    handleContractChange={handleContractChange}
                    title="Work Schedule Templates"
                    employeeId={employeeId}
                    employee={employee}
                    handleCloseButton={handleCloseButton}
                    defaultHolidays={holidays}
                    message={message}
                    colorTheme={colorTheme}
                />
            ),
            icon: <EventNoteOutlinedIcon />,
        },
    ];
    if (isManager) {
        tabs = [
            ...tabs,
            {
            id: "benefits",
            title: "Benefits",
            component: (
                <Benefits
                    contractId={contractId}
                    contract={contract}
                    contracts={contracts}
                    handleContractChange={handleContractChange}
                    title="Benefits"
                    employeeId={employeeId}
                    employee={employee}
                    handleCloseButton={handleCloseButton}
                    message={message}
                />
            ),
            icon: <RedeemOutlinedIcon/>,
        },
            {
                id: "teams",
                title: "Teams",
                component: (
                    <Teams
                        contractId={contractId}
                        contract={contract}
                        contracts={contracts}
                        handleContractChange={handleContractChange}
                        title="Teams"
                        employeeId={employeeId}
                        handleCloseButton={handleCloseButton}
                        teams={teams}
                        isApproval={isApproval}
                        message={message}
                        employee={employee}
                        handelRefreshEmployeeList={handelRefreshEmployeeList}
                        colorTheme={colorTheme}
                        setEmployeeUpdated={setEmployeeUpdated}
                    />
                ),
                icon: <PeopleOutlineIcon/>,
            },
            {
                id: "time_off",
                title: "Time off",
                component: (
                    <TimeOff
                        contractId={contractId}
                        contract={contract}
                        contracts={contracts}
                        handleContractChange={handleContractChange}
                        title="Time off"
                        employeeId={employeeId}
                        handleCloseButton={handleCloseButton}
                        message={message}
                        colorTheme={colorTheme}
                        balanceTab={balanceTab}
                        innerTabIndex={innerTabIndex?.timeOff}
                        onInnerTabChange={onInnerTabChange?.("timeOff")}
                    />
                ),
                icon: <PoolOutlinedIcon/>,
            },
            {
                id: "wfh",
                title: "WFH",
                component: (
                    <Wfh
                        contractId={contractId}
                        contract={contract}
                        contracts={contracts}
                        handleContractChange={handleContractChange}
                        title="WFH"
                        employeeId={employeeId}
                        handleCloseButton={handleCloseButton}
                        message={message}
                        colorTheme={colorTheme}
                        innerTabIndex={innerTabIndex?.wfh}
                        onInnerTabChange={onInnerTabChange?.("wfh")}
                    />
                ),
                icon: <HouseOutlinedIcon/>,
            },
            {
                id: "customize_time_off_policy",
                title: "Customize Policies",
                component: (
                    <CustomizeTimeOff
                        title="Customize Policies"
                        setEmployeeUpdated={setEmployeeUpdated}
                        employeeId={employeeId}
                        handleCloseButton={handleCloseButton}
                    />
                ),
                icon: <PoolOutlinedIcon/>,
            },
            {
                id: "attendance_report",
                title: "Attendance Report",
                component: (
                    <AttendanceReport
                        contractId={contractId}
                        contract={contract}
                        contracts={contracts}
                        handleContractChange={handleContractChange}
                        title="Attendance Report"
                        employeeId={employeeId}
                        handleCloseButton={handleCloseButton}
                        message={message}
                        colorTheme={colorTheme}
                    />
                ),
                icon: <AccessAlarmOutlinedIcon/>,
            },
        ];
    }
    tabs = filter(tabs, (el) => includes(profileTabs, el.id))

    if (employee.activated) {
        tabs.push({
            id: "deactivate_profile",
            title: "Deactivate Profile",
            component: (
                <DeactivateProfile
                    title="Deactivate Profile"
                    employeeId={employeeId}
                    setEmployeeUpdated={setEmployeeUpdated}
                    handleCloseButton={handleCloseButton}
                    message={message}
                    employee={employee}
                    handleTabOnChange={handleTabOnChange}
                    handelRefreshEmployeeList={handelRefreshEmployeeList}
                    colorTheme={colorTheme}
                />
            ),
            icon: <PersonOffSharpIcon />,
            className: "text-danger",
        });
    } else {
        tabs.push(
            {
                id: "delete_profile",
                title: "Delete Profile",
                component: (
                    <DeleteProfile
                        title="Delete Profile"
                        employeeId={employeeId}
                        setEmployeeUpdated={setEmployeeUpdated}
                        handleCloseButton={handleCloseButton}
                        message={message}
                        employee={employee}
                        handelRefreshEmployeeList={handelRefreshEmployeeList}
                        colorTheme={colorTheme}
                    />
                ),
                icon: <PersonRemoveSharpIcon />,
                className: "text-danger",
            },
            {
                id: "reactivate_profile",
                title: "Reactivate Profile",
                component: (
                    <ReactivateProfile
                        title="Reactivate Profile"
                        employeeId={employeeId}
                        setEmployeeUpdated={setEmployeeUpdated}
                        handleCloseButton={handleCloseButton}
                        handleTabOnChange={handleTabOnChange}
                        message={message}
                        employee={employee}
                        handelRefreshEmployeeList={handelRefreshEmployeeList}
                        colorTheme={colorTheme}
                    />
                ),
                icon: <PersonSharpIcon />,
                className: "text-success",
            }
        );
    }

    return tabs;
};

function EmployeeDetails(props) {
    const { employeeId, handleCloseButton, handelRefreshEmployeeList, profileTabs, tabIndex, balanceTab } = props;
    const isManager = getIsManager();
    const [employeeUpdated, setEmployeeUpdated] = useState(false);
    const [contracts, setContracts] = useState([]);
    const [selectedContractId, setSelectedContractId] = useState(undefined);
    const [inProgress, setInProgress] = useState(true);
    const [currentTabIndex, setCurrentTabIndex] = useState(isManager? tabIndex || 0 : 0);
    const [profilePicture, setProfilePicture] = useState(undefined);
    const [employeeDrawerList, setEmployeeDrawerList] = useState([]);
    // The fetched employee payload is kept in state so the tab list can be rebuilt from it
    // whenever the contract selection changes, without re-requesting the employee.
    const [employeeData, setEmployeeData] = useState(undefined);
    const selectedTheme = useTheme();
    const colorTheme = selectedTheme?.palette?.primary?.main;
    const handleTabOnChange = (newTabIndex) => {
        setCurrentTabIndex(newTabIndex);
    };

    // Sub-tab selections for the sections that have their own tab strip. They live here, ABOVE the
    // contract key on LeftTabsDrawer below, because that key remounts everything under it whenever
    // the contract changes -- which threw the sub-tab away and dropped the user back on the first
    // one. Most visibly: switching contract from the Balance tab landed on "time off requests".
    // Keeping the index up here survives the remount; the remount itself is still wanted, so no
    // figure from the previous contract can linger on screen.
    const [innerTabIndex, setInnerTabIndex] = useState({});
    const onInnerTabChange = (section) => (index) =>
        setInnerTabIndex((previous) => ({ ...previous, [section]: index }));

    // The resolved contract object; tabs need its dates, not just its id, to bound records.
    const selectedContract = (contracts || []).find((c) => c?.id === selectedContractId);

    const setProfileDrawerList = (basicInformationWithManager, employee) => {
        setEmployeeDrawerList(
            getEmployeeDrawerList(
                employeeId,
                handleCloseButton,
                employee,
                basicInformationWithManager,
                setEmployeeUpdated,
                handleTabOnChange,
                handelRefreshEmployeeList,
                profileTabs,
                colorTheme,
                balanceTab,
                selectedContractId,
                contracts,
                setSelectedContractId,
                selectedContract,
                innerTabIndex,
                onInnerTabChange
            )
        );
    }
    /**
     * Loads the employee's contracts and decides which one is selected.
     *
     * Selection rule, in order: the contract covering today, else the most recent one. With a
     * single contract the selector is not rendered at all, so the employee's experience is
     * unchanged. With no contracts the page behaves exactly as it did before this feature —
     * selectedContractId stays undefined and no contractId is sent to any API.
     */
    const fetchContracts = async () => {
        try {
            const response = await getEmployeeContracts(employeeId);
            const employeeContracts = response?.data || [];
            setContracts(employeeContracts);

            if (!employeeContracts.length) {
                setSelectedContractId(undefined);
                return;
            }
            const current = employeeContracts.find((contract) => contract.current);
            setSelectedContractId((current || employeeContracts[employeeContracts.length - 1])?.id);
        } catch (e) {
            // Contracts are additive: if they cannot be loaded the page must still work, showing
            // the whole-employee view it has always shown.
            setContracts([]);
            setSelectedContractId(undefined);
        }
    };

    const fetchData = async () => {
        setInProgress(true);
        try {
            let response = await getEmployeeDetails(employeeId);
            const { basicInformationWithManager, employee } = response.data;
            setProfilePicture(employee.profilePicture);
            setEmployeeData({ basicInformationWithManager, employee });
        } catch (e) {
            //setPaginationData({...paginationData, next: false });
        }
        setInProgress(false);
    };

    useEffect(() => {
        fetchContracts();
        fetchData();
    }, []);

    // Rebuild the tabs whenever the employee payload, the contract list or the selected contract
    // changes. Deriving the list from state rather than from inside the fetch callback avoids the
    // ordering problem where the employee request resolves before the contracts request and the
    // tabs get built with an empty contract list.
    useEffect(() => {
        if (employeeData) {
            setProfileDrawerList(employeeData.basicInformationWithManager, employeeData.employee);
        }
    }, [employeeData, contracts, selectedContractId, innerTabIndex]);

    useEffect(() => {
        if (employeeUpdated) {
            fetchData();
            setEmployeeUpdated(false)

        }
    }, [employeeUpdated]);

    return (
        <div className="c-employee-details">
            {inProgress ? (
                <Loading></Loading>
            ) : (
                <>
                    {!!employeeDrawerList.length && (
                        <LeftTabsDrawer
                            // Remounting on contract change guarantees no figure from the previous
                            // contract can survive on screen while the new one loads.
                            key={`employee-${employeeId}-contract-${selectedContractId ?? "all"}`}
                            currentTabIndex={currentTabIndex}
                            handleTabOnChange={handleTabOnChange}
                            drawerList={employeeDrawerList}
                            orientation="vertical"
                            profilePicture={profilePicture}
                            employeeId={employeeId}
                            handelRefreshEmployeeList={handelRefreshEmployeeList}
                        ></LeftTabsDrawer>
                    )}
                </>
            )}
        </div>
    );
}

export default EmployeeDetails;
